// All env access on the landing site funnels through this module — same convention as apps/client.
// Vite exposes only `VITE_*` env vars to the client bundle.

/** Public Mavori control-plane API origin — not a secret; ships in the client bundle. */
export const API_BASE_URL = "https://api.mavori.ai";

type LandingEnv = {
  reportUrl: string;
  reportApiUrl: string;
  /** The in-app SPA origin the hero composer hands off to once launched. */
  appUrl: string;
  /** Launch switch: the deployed Worker owns this per environment; local Vite
   * builds can fall back to VITE_LAUNCHED=1. */
  launched: boolean;
  /** PostHog project key. Injected at request time by the landing Worker (see
   * worker/index.js::serializeRuntimeConfig). Empty => analytics is a no-op. */
  posthogKey: string;
  /** PostHog ingest host. Defaults to the US cloud endpoint. */
  posthogHost: string;
  /** Region gate decided by the Worker from request.cf.country (EU/EEA/UK =>
   * false). Absent (local dev, no Worker) defaults to true — analytics still
   * no-ops there without a key. */
  analyticsAllowed: boolean;
  /** Whether this visitor's region requires prior consent before anything is
   * stored on their device for a non-essential purpose (EU/EEA/UK, ePrivacy
   * Art. 5(3)). Decided by the Worker from request.cf.country. Absent (local
   * dev, no Worker) defaults to false. */
  consentRequired: boolean;
  /** In-app browser source (wechat/tiktok/instagram/...) detected by the Worker
   * from the User-Agent, or "" for an ordinary browser. Attribution signal for
   * bare-URL social placements where UTM + referrer are both absent. */
  inappSource: string;
};

type RuntimeConfigWindow = {
  __APP_RUNTIME_CONFIG__?: {
    launched?: unknown;
    launchOverrideAllowed?: unknown;
    appUrl?: unknown;
    posthogKey?: unknown;
    posthogHost?: unknown;
    analyticsAllowed?: unknown;
    consentRequired?: unknown;
    inappSource?: unknown;
  };
};

function readRuntimeConfig(): RuntimeConfigWindow["__APP_RUNTIME_CONFIG__"] {
  if (typeof globalThis === "undefined") return undefined;
  return (globalThis as unknown as RuntimeConfigWindow).__APP_RUNTIME_CONFIG__;
}

function envString(value: unknown): string {
  return typeof value === "string" && value.trim().length > 0 ? value : "";
}

function launchOverride(allowed: boolean): boolean | undefined {
  if (!allowed || typeof globalThis === "undefined" || !("location" in globalThis)) {
    return undefined;
  }
  const value = new URLSearchParams(globalThis.location.search).get("launched")?.toLowerCase();
  if (value === "true" || value === "1") return true;
  if (value === "false" || value === "0") return false;
  return undefined;
}

function readEnv(): LandingEnv {
  const env = import.meta.env;
  const runtime = readRuntimeConfig();
  const launchedByEnvironment =
    typeof runtime?.launched === "boolean"
      ? runtime.launched
      : env.VITE_LAUNCHED === "1" || env.VITE_LAUNCHED === "true";
  const launchedOverride = launchOverride(
    runtime?.launchOverrideAllowed === true || import.meta.env.DEV,
  );
  return {
    reportUrl: env.VITE_REPORT_URL ?? "https://report.mavori.ai",
    // The report backend (Fly-hosted FastAPI) fronted by Cloudflare DNS. We
    // only use its public `/api/stocks/search` endpoint from the landing.
    reportApiUrl: env.VITE_REPORT_API_URL ?? "https://reportapi.mavori.ai",
    appUrl: envString(runtime?.appUrl) || env.VITE_APP_URL || "https://app.mavori.ai",
    launched: launchedOverride ?? launchedByEnvironment,
    posthogKey: envString(runtime?.posthogKey),
    posthogHost: envString(runtime?.posthogHost),
    // Only an explicit `false` from the Worker disables analytics; absent
    // (local dev) defaults to allowed.
    analyticsAllowed: runtime?.analyticsAllowed !== false,
    consentRequired: runtime?.consentRequired === true,
    inappSource: envString(runtime?.inappSource),
  };
}

export const env: LandingEnv = readEnv();

export function reportUrl(path = "/"): string {
  return joinUrl(env.reportUrl, path);
}

export function reportApiUrl(path = "/"): string {
  return joinUrl(env.reportApiUrl, path);
}

/**
 * Sign-in URL on the app origin carrying the hero prompt (launched mode only).
 * The `?prompt=` param is the cross-origin transport: the app's SignInScreen
 * stashes it into its own localStorage (shared/lib/pendingComposerPrompt.ts)
 * so it survives the OAuth/email round-trip, then chat prefills it — never
 * auto-sends. No server storage involved.
 *
 * `ref` carries a referral code the visitor arrived with straight through to
 * signup, the same way WaitlistForm.tsx already does for the waitlist-join
 * path — a referral link used to sign up must count as a referral too, not
 * only one used to join the waitlist.
 */
export function appSignInUrl(prompt: string, ref?: string | null): string {
  const params = new URLSearchParams({ mode: "signup", next: "/chat" });
  const trimmed = prompt.trim();
  if (trimmed.length > 0) params.set("prompt", trimmed);
  if (ref) params.set("ref", ref);
  return `${joinUrl(env.appUrl, "/sign-in")}?${params.toString()}`;
}

/** Direct app route for launch-state navigation such as Sign in. */
export function appUrl(path = "/", referralCode?: string | null): string {
  const url = new URL(joinUrl(env.appUrl, path));
  if (referralCode) url.searchParams.set("ref", referralCode);
  return url.toString();
}

export function apiUrl(path = "/"): string {
  // Production posts same-origin: the landing Worker proxies /api/v1/public/*
  // to the API Worker over a service binding. The cross-origin fetch to
  // api.mavori.ai is dropped in some in-app browsers / filtered networks
  // (e.g. WeChat webviews) even when the page loads. Dev keeps the absolute
  // origin — the Vite server has no proxy.
  return import.meta.env.DEV ? joinUrl(API_BASE_URL, path) : path;
}

function joinUrl(base: string, path: string): string {
  const trimmed = base.replace(/\/+$/, "");
  const tail = path.startsWith("/") ? path : `/${path}`;
  return `${trimmed}${tail}`;
}
